import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ErrorService } from './error.service';

/**
 * Options accepted by `HttpClient` verb methods. Mirrors the subset
 * Angular's HttpClient understands so callers can pass typed params,
 * headers, etc. without leaking `any`.
 */
export interface HttpOptions {
  headers?: HttpHeaders | Record<string, string | string[]>;
  params?: HttpParams | Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>>;
  context?: HttpContext;
  observe?: 'body';
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
}

/**
 * Generic HTTP wrapper. Every verb is parameterised by its response
 * type (and `post`/`put` by their body type). Callers default the
 * generic to `unknown` rather than `any` — they must narrow before
 * dereferencing.
 *
 * Per implementation-guidelines.md §3.1.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiHttpService {
  private readonly baseUrl: string = environment.BASEURL;
  private readonly http = inject(HttpClient);
  private readonly errorService = inject(ErrorService);

  get<TResponse = unknown>(url: string, options?: HttpOptions): Observable<TResponse> {
    return this.http.get<TResponse>(this.baseUrl + url, options).pipe(catchError((err) => this.report(err)));
  }

  post<TBody = unknown, TResponse = unknown>(
    url: string,
    body: TBody,
    options?: HttpOptions,
  ): Observable<TResponse> {
    return this.http.post<TResponse>(this.baseUrl + url, body, options).pipe(catchError((err) => this.report(err)));
  }

  put<TBody = unknown, TResponse = unknown>(
    url: string,
    body: TBody,
    options?: HttpOptions,
  ): Observable<TResponse> {
    return this.http.put<TResponse>(this.baseUrl + url, body, options).pipe(catchError((err) => this.report(err)));
  }

  patch<TBody = unknown, TResponse = unknown>(
    url: string,
    body: TBody,
    options?: HttpOptions,
  ): Observable<TResponse> {
    return this.http.patch<TResponse>(this.baseUrl + url, body, options).pipe(catchError((err) => this.report(err)));
  }

  delete<TResponse = unknown>(url: string, options?: HttpOptions): Observable<TResponse> {
    return this.http.delete<TResponse>(this.baseUrl + url, options).pipe(catchError((err) => this.report(err)));
  }

  private report(err: HttpErrorResponse): Observable<never> {
    this.errorService.handleError(err);
    return throwError(() => err);
  }
}
